import type { CustomerRiskDTO } from "@/modules/customers/domain/types";
import { cn } from "@/lib/utils";
import { formatLak } from "@/shared/lib/currency";
import {
  customerRiskTierMeta,
  formatDepositHint,
  suggestedDepositAmount,
} from "./customer-risk";

type CustomerCodRiskBannerProps = {
  risk: CustomerRiskDTO;
  className?: string;
  /** Larger banner under customer block (default true for prominence). */
  prominent?: boolean;
};

export function CustomerCodRiskBanner({
  risk,
  className,
  prominent = false,
}: CustomerCodRiskBannerProps) {
  const meta = customerRiskTierMeta(risk.tier);
  const depositHint = formatDepositHint(risk);
  const depositAmount = suggestedDepositAmount(risk);
  const topReasons = risk.reasons.slice(0, prominent ? 3 : 2);

  return (
    <div
      className={cn(
        "rounded-xl border",
        prominent ? "px-4 py-3.5 shadow-sm" : "px-3 py-2.5 text-sm",
        meta.bannerClass,
        className,
      )}
      role="status"
    >
      <p
        className={cn(
          "font-medium leading-snug",
          prominent && "font-display text-base sm:text-lg",
        )}
      >
        {meta.actionLabel}
        {depositHint ? (
          <span className={cn(!prominent && "text-sm")}>
            {" · "}
            {depositHint}
          </span>
        ) : null}
        <span
          className={cn(
            "ml-2 font-normal opacity-80 tabular-nums",
            prominent ? "text-sm" : "text-xs",
          )}
        >
          ({risk.score})
        </span>
      </p>
      {depositAmount != null && depositAmount > 0 ? (
        <p
          className={cn(
            "mt-1 font-semibold tabular-nums",
            prominent ? "text-base" : "text-sm",
          )}
        >
          ~{formatLak(depositAmount)}
          {risk.suggestedDepositPct < 100
            ? ` (${risk.suggestedDepositPct}%)`
            : " (100%)"}
        </p>
      ) : null}
      {topReasons.length > 0 ? (
        <ul
          className={cn(
            "mt-1.5 flex flex-col gap-0.5 opacity-90",
            prominent ? "text-sm" : "text-xs",
          )}
        >
          {topReasons.map((reason) => (
            <li key={reason}>· {reason}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
